import React, { useState, useCallback, useMemo } from "react";
import { Box, LinearProgress, Typography, Divider } from "@material-ui/core";
import LanguageSwitch from "./LanguageSwitch";
import { TranslationRow } from "./TranslationsRow";
import { computeLanguageCompletion, sortTableData } from "./utils/helpers";
import { withStyles } from "@material-ui/core";
import { isFunction } from "../../utils/mappersUtils/guards";
import {
  deleteTranslation,
  syncTranslations,
  syncTranslationsBulk,
} from "./translationsClient";
import AITranslationButton from "./AITranslationButton";
import { DisplayMessage } from "../../services/helpers";
import { translateChecklistTemplateWithAI } from "../WorkshopPlanning/General/ChecklistTemplates/services/checklistTemplatesApisService";
import { buildAITranslationRequest, mapTableDataForSyncRequest } from "./utils/requestBuilders";

const BorderLinearProgress = withStyles(() => ({
  root: {
    height: 8,
    borderRadius: 5,
  },
  bar: {
    borderRadius: 5,
  },
}))(LinearProgress);

/**
 * TranslationsTable Component
 *
 * Expects a prop "data" which is an array of translation row objects.
 *
 * Each translation row has the following structure:
 *
 * {
 *   id: number,                // Unique identifier for the row.
 *   type: string,              // Examples: "TEMPLATE", "SEGMENT", or any other entity
 *                                            defined by the sync API.
 *   baseTranslations: {        // The original text from the base language.
 *     description: string,     // For TEMPLATE/SEGMENT rows, the main text.
 *     shortDescription?: string
 *     ...
 *   },
 *   groupKeys?: string[],      // Only for ITEM rows that require multiple fields for sync.
 *                              // E.g. ["description", "shortDescription"].
 *   translations: {            // An object keyed by language codes.
 *     [langCode: string]: {    // For TEMPLATE and SEGMENT rows:
 *       item: {
 *         key: string,         // Typically "description".
 *         value: string        // The translated text (empty string if missing).
 *       }
 *       // For ITEM rows:
 *       // group: [
 *       //   { key: "description", value: string },
 *       //   { key: "shortDescription", value: string }
 *       // ]
 *     }
 *   }
 * }
 *
 * The component uses this structure to:
 * - Compute translation completion percentages via computeLanguageCompletion().
 * - Render a LanguageSwitch component.
 * - Render each row via TranslationRow (which uses "groupKeys" to determine
 *   if an ITEM row should render one or more text fields).
 */

export default function TranslationsTable({
  data = [],
  languages = [],
  defaultLang = "nl",
  onTranslationSync,
  onDeleteTranslation,
  baseUrl,
  syncTranslationKeys,
  resources,
  hasTranslationLicence,
}) {
  const [selectedLang, setSelectedLang] = useState(defaultLang);
  const [tableData, setTableData] = useState(() =>
    sortTableData(data, selectedLang)
  );
  const [aiTranslating, setAiTranslating] = useState(false);

  // Compute the completion percentage per language.
  const computedLanguages = useMemo(
    () => computeLanguageCompletion(languages, tableData),
    [languages, tableData]
  );

  const overallProgress = useMemo(() => {
    if (computedLanguages.length === 0) return 0;
    const total = computedLanguages.reduce(
      (sum, lang) => sum + lang.percentage,
      0
    );
    return Math.round(total / computedLanguages.length);
  }, [computedLanguages]);

  const totalKeys = useMemo(() => {
    return tableData.reduce((acc, row) => {
      if (
        row.groupKeys &&
        Array.isArray(row.groupKeys) &&
        row.groupKeys.length > 0
      ) {
        return acc + row.groupKeys.length;
      }
      return acc + 1;
    }, 0);
  }, [tableData]);

  // Check if all translations for the current language are already filled
  const allTranslationsFilled = useMemo(() => {
    return tableData.every(row => {
      const langData = row.translations[selectedLang];
      if (!langData) return false;
      
      if (langData.item) {
        // For single item translations, check if value is not empty
        return langData.item.value && langData.item.value.trim().length > 0;
      } else if (langData.group) {
        // For group translations, check if all group items have values
        return langData.group.every(item => item.value && item.value.trim().length > 0);
      }
      
      return false;
    });
  }, [tableData, selectedLang]);

  const handleLanguageChange = (langCode) => {
    setSelectedLang(langCode);
  };

  const handleSync = useCallback(
    async (syncEvent, updatedRowData) => {
      setTableData((prev) =>
        prev.map((row) => (row.id === updatedRowData.id ? updatedRowData : row))
      );

      const { rowId, rowType, language, translations } = syncEvent;

      const data = {
        id: rowId,
        type: rowType,
        language,
      };

      syncTranslationKeys.forEach((key) => {
        data[key] = translations[key];
      });

      const result = await syncTranslations(JSON.stringify(data), baseUrl);

      if (!result) {
        return { success: false };
      }

      if (isFunction(onTranslationSync)) {
        onTranslationSync(result);
      }

      return { success: true };
    },
    [onTranslationSync, baseUrl, syncTranslationKeys]
  );

  const syncAllTranslations = async (updatedValues) => {
    const mappedData = mapTableDataForSyncRequest(updatedValues.newTableData, updatedValues.changedEntitesIds, selectedLang);
    const result = await syncTranslationsBulk(
      JSON.stringify(mappedData),
      baseUrl
    );
    if (!result) {
      DisplayMessage(resources["TranslationSaveFailed"], "warning");
    }
    DisplayMessage(resources["TranslationSaved"], "success");
    if (isFunction(onTranslationSync)) {
      onTranslationSync(result);
    }
  };

  const handleDelete = useCallback(
    async (deleteEvent, updatedRowData) => {
      const { rowId, rowType, language } = deleteEvent;

      const data = {
        id: rowId,
        type: rowType,
        language,
      };

      let deleteResult;

      try {
        deleteResult = await deleteTranslation(JSON.stringify(data), baseUrl);
      } catch (e) {
        return { success: false };
      }

      if (!deleteResult) {
        return { success: false };
      }

      // Only update table data after successful API call
      setTableData((prev) =>
        prev.map((row) => (row.id === updatedRowData.id ? updatedRowData : row))
      );

      if (isFunction(onDeleteTranslation)) {
        onDeleteTranslation(deleteResult);
      }

      return { success: true };
    },
    [onDeleteTranslation, baseUrl]
  );

  const mergeAITranslations = (aiResponse) => {
    const newTableData = JSON.parse(JSON.stringify(tableData));
    const changedEntitesIds = [];
    Object.entries(newTableData).forEach(([_, object]) => {
      const entity = aiResponse.find((entry) => object.id === entry.entityId);
      if (entity) {
        changedEntitesIds.push(object.id);
        Object.keys(entity.translations).forEach((key) => {
          const mappedTranslations = entity.translations[key].map(
            (translation) => {
              let translationValue = ''
              let maxLength = 0;
              let wasTrimmed = false;
              
              if (translation.label === 'description') {
                maxLength = 256;
                translationValue = translation.text.substring(0, maxLength);
                wasTrimmed = translation.text.length > maxLength;
              } 
              else{
                maxLength = 40;
                translationValue = translation.text.substring(0, maxLength);
                wasTrimmed = translation.text.length > maxLength;
              }
              
              return {
                key: translation.label,
                value: translationValue,
                wasTrimmed: wasTrimmed,
                originalLength: translation.text.length,
                maxLength: maxLength
              };
            }
          );
          if (object.type === "ITEM") {
            object.translations[key].group = mappedTranslations;
          } else {
            object.translations[key].item = mappedTranslations.at(0);
          }
        });
      }
    });

    return {
      changedEntitesIds: changedEntitesIds,
      newTableData: [...newTableData],
    };
  };

  const handleTranslateWithAI = async () => {
    setAiTranslating(true);
    let updatedValues;
    try {
      const req = buildAITranslationRequest(selectedLang, tableData);
      if (req.entities.length !== 0) {
        const aiResponse = await translateChecklistTemplateWithAI(req);
        updatedValues = mergeAITranslations(aiResponse);
        setTableData(updatedValues.newTableData);
        DisplayMessage(resources["aiTranslationSucceeded"], "success");
        syncAllTranslations(updatedValues);
      }
    } catch (e) {
      DisplayMessage(resources["aiTranslationFailed"], "error");
    }
    setAiTranslating(false);
  };

  return (
    <>
      <Box mb={2}>
        <Box display="flex" alignItems="baseline">
          <Typography variant="h4" component="span" color="textPrimary">
            {overallProgress}%
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="textSecondary"
            style={{ margin: "0 8px" }}
          >
            {resources["OverallTranslated"]}
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            style={{ margin: "0 8px" }}
          />

          <Typography variant="h4" component="span" color="textPrimary">
            {totalKeys}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            color="textSecondary"
            style={{ margin: "0 8px" }}
          >
            {resources["TotalKeys"]}
          </Typography>
        </Box>

        <Box mt={1}>
          <BorderLinearProgress variant="determinate" value={overallProgress} />
        </Box>
      </Box>

      <Box mb="26px" sx={{ display: "flex" }}>
        <LanguageSwitch
          languages={computedLanguages}
          selectedLang={selectedLang}
          onChange={handleLanguageChange}
          id="translations-table"
        />
        {hasTranslationLicence && (
          <AITranslationButton
            onClick={handleTranslateWithAI}
            buttonDisabled={aiTranslating || allTranslationsFilled}
            isTranslating={aiTranslating}
            allTranslationsFilled={allTranslationsFilled}
            resources={resources}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column">
        {tableData.map((rowData, index) => (
          <Box key={rowData.id} mb={index !== tableData.length - 1 ? 2 : 0}>
            <TranslationRow
              rowData={rowData}
              selectedLang={selectedLang}
              onSync={handleSync}
              onDelete={handleDelete}
              resources={resources}
            />
          </Box>
        ))}
      </Box>
    </>
  );
}
